import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { motion, useMotionTemplate, useScroll, useSpring, useTransform } from "framer-motion";

// Bausteine für die mutige Vorschau: Bewegung, Scroll-Story, Parallax

// Sanftes Scrollen mit Nachlauf (wie bei großen Agentur-Seiten)
export const useSmoothScroll = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
      syncTouch: false,
    });
    let id;
    const raf = (zeit) => {
      lenis.raf(zeit);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, []);
};

export const ScrollFortschritt = () => {
  const { scrollYProgress } = useScroll();
  const breite = useSpring(scrollYProgress, { stiffness: 90, damping: 28, restDelta: 0.001 });
  return (
    <motion.div data-testid="wow-scroll-fortschritt"
      className="fixed top-0 left-0 right-0 h-[3px] bg-[#C8743F] origin-left z-[60]"
      style={{ scaleX: breite }} />
  );
};

// Headline, die Wort für Wort aus der Maske nach oben kommt
export const WortReveal = ({ text, className = "", delay = 0, testid }) => (
  <span data-testid={testid} className={`inline-block ${className}`}>
    {text.split(" ").map((wort, i) => (
      <span key={`${wort}-${i}`} className="inline-block overflow-hidden align-bottom pb-[0.12em]">
        <motion.span className="inline-block"
          initial={{ y: "115%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          transition={{ duration: 0.95, delay: delay + i * 0.07, ease: [0.16, 1, 0.3, 1] }}>
          {wort}&nbsp;
        </motion.span>
      </span>
    ))}
  </span>
);

// Endlos laufendes Band mit Begriffen
export const Laufband = ({ worte, testid }) => (
  <div data-testid={testid} className="relative overflow-hidden py-5 select-none">
    <motion.div className="flex gap-10 whitespace-nowrap will-change-transform"
      animate={{ x: ["0%", "-50%"] }}
      transition={{ duration: 34, ease: "linear", repeat: Infinity }}>
      {[...worte, ...worte, ...worte, ...worte].map((w, i) => (
        <span key={i} className="inline-flex items-center gap-10 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#9B8878]">
          {w}
          <span className="w-1.5 h-1.5 rotate-45 border border-[#C8743F]" />
        </span>
      ))}
    </motion.div>
  </div>
);

// Bild, das sich beim Scrollen langsamer bewegt — mit Federung, damit nichts hakt
export const ParallaxBild = ({ src, alt, hoehe = "h-[62vh]", staerke = 45, className = "", testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const weich = useSpring(scrollYProgress, { stiffness: 70, damping: 26, mass: 0.4, restDelta: 0.0005 });
  const y = useTransform(weich, [0, 1], [-staerke, staerke]);
  return (
    <div ref={ref} data-testid={testid}
      className={`relative overflow-hidden ${hoehe} ${className}`}>
      <motion.img src={src} alt={alt} loading="lazy" style={{ y, willChange: "transform" }}
        className="absolute inset-0 w-full h-[112%] -top-[6%] object-cover" />
    </div>
  );
};

// Eine Szene der Scroll-Story: Bild komplett sichtbar, weicher Hintergrund, sanfte Drift
const Szene = ({ szene, index, anzahl, fortschritt }) => {
  const schritt = 1 / anzahl;
  const start = index * schritt;
  const bildOpacity = useTransform(
    fortschritt,
    [start - schritt * 0.2, start + schritt * 0.1, start + schritt * 0.9, start + schritt * 1.2],
    index === 0 ? [1, 1, 1, 0] : [0, 1, 1, 0],
  );
  const scale = useTransform(fortschritt, [start - schritt * 0.3, start + schritt * 1.3], [1.04, 1.0]);
  const y = useTransform(fortschritt, [start - schritt * 0.3, start + schritt * 1.3], [26, -26]);
  return (
    <motion.div className="absolute inset-0" style={{ opacity: bildOpacity, willChange: "opacity" }}>
      <div className="absolute inset-0 bg-cover bg-center blur-2xl scale-110 opacity-45"
        style={{ backgroundImage: `url('${szene.bild}')` }} />
      <div className="absolute inset-0 bg-[#15120F]/35" />
      <motion.img src={szene.bild} alt={szene.titel} loading="lazy"
        style={{ scale, y, willChange: "transform",
          maskImage: "linear-gradient(to right, transparent 0%, black 16%, black 88%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 16%, black 88%, transparent 100%)" }}
        className="absolute inset-0 w-full h-full object-contain object-center sm:object-right" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#15120F] via-[#15120F]/45 to-[#15120F]/35 sm:bg-gradient-to-r sm:from-[#15120F] sm:via-[#15120F]/70 sm:to-transparent" />
    </motion.div>
  );
};

const SzenenText = ({ szene, index, anzahl, fortschritt }) => {
  const schritt = 1 / anzahl;
  const start = index * schritt;
  const opacity = useTransform(
    fortschritt,
    [start - schritt * 0.02, start + schritt * 0.16, start + schritt * 0.84, start + schritt * 1.02],
    index === 0 ? [1, 1, 1, 0] : [0, 1, 1, 0],
  );
  const y = useTransform(
    fortschritt,
    [start - schritt * 0.02, start + schritt * 0.2, start + schritt * 0.8, start + schritt * 1.02],
    [34, 0, 0, -34],
  );
  return (
    <motion.div style={{ opacity, y, willChange: "transform, opacity" }}
      className="absolute inset-x-0 max-w-xl" data-testid={`story-text-${index}`}>
      <p className="text-[11px] uppercase tracking-[0.3em] text-[#F3CDAC] text-klar">{szene.kicker}</p>
      <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#FFFBF4] mt-4 leading-[1.08] text-klar-stark">
        {szene.titel}
      </h3>
      <p className="text-sm sm:text-base text-[#F6EFE6] mt-5 font-normal leading-relaxed text-klar">{szene.text}</p>

      {szene.punkte && (
        <ul className="mt-7 space-y-3 border-t border-white/15 pt-6">
          {szene.punkte.map((pkt) => (
            <li key={pkt} className="flex items-start gap-3 text-sm text-[#FBF5EC] font-normal text-klar">
              <span className="mt-[9px] w-4 h-px bg-[#C8743F] shrink-0" />
              {pkt}
            </li>
          ))}
        </ul>
      )}

      {szene.fussnote && (
        <p className="mt-6 text-xs text-[#E3D3C2] italic text-klar">{szene.fussnote}</p>
      )}
    </motion.div>
  );
};

const Punkt = ({ index, anzahl, fortschritt }) => {
  const schritt = 1 / anzahl;
  const start = index * schritt;
  const breite = useTransform(
    fortschritt,
    [start - schritt * 0.05, start + schritt * 0.15, start + schritt * 0.85, start + schritt * 1.05],
    [24, 48, 48, 24],
  );
  const opacity = useTransform(
    fortschritt,
    [start - schritt * 0.05, start + schritt * 0.15, start + schritt * 0.85, start + schritt * 1.05],
    [0.25, 1, 1, 0.25],
  );
  return <motion.span className="h-[2px] bg-[#C8743F]" style={{ width: breite, opacity }} />;
};

// Scroll-Story: Bilder wechseln, während man an einem festen Abschnitt vorbeiscrollt
export const BildStory = ({ szenen, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const weich = useSpring(scrollYProgress, { stiffness: 42, damping: 22, mass: 0.28, restDelta: 0.0005 });

  return (
    <section ref={ref} data-testid={testid}
      style={{ height: `${szenen.length * 150}vh` }} className="relative bg-[#15120F]">
      <div className="sticky top-0 h-screen overflow-hidden">
        {szenen.map((s, i) => (
          <Szene key={s.bild} szene={s} index={i} anzahl={szenen.length} fortschritt={weich} />
        ))}

        <div className="relative h-full flex items-end sm:items-center">
          <div className="w-full max-w-6xl mx-auto px-6 md:px-12 pt-28 pb-24 sm:pt-24 sm:pb-10">
            <div className="relative h-[430px] sm:h-[min(440px,54vh)]">
              {szenen.map((s, i) => (
                <SzenenText key={s.bild} szene={s} index={i} anzahl={szenen.length} fortschritt={weich} />
              ))}
            </div>
            <div className="flex gap-2 mt-4 sm:mt-6">
              {szenen.map((s, i) => (
                <Punkt key={s.bild} index={i} anzahl={szenen.length} fortschritt={weich} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Bild, das beim Scrollen wie ein Vorhang aufgezogen wird
export const VorhangBild = ({ src, alt, hoehe = "h-[62vh]", staerke = 40, ausrichtung = "object-center", testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const weich = useSpring(scrollYProgress, { stiffness: 70, damping: 26, mass: 0.4, restDelta: 0.0005 });
  const y = useTransform(weich, [0, 1], [-staerke, staerke]);
  const rest = useTransform(weich, [0.05, 0.32], [100, 0], { clamp: true });
  const vorhang = useMotionTemplate`inset(0% 0% ${rest}% 0%)`;
  return (
    <motion.div ref={ref} data-testid={testid}
      className={`relative overflow-hidden ${hoehe}`}
      style={{ clipPath: vorhang, WebkitClipPath: vorhang }}>
      <motion.img src={src} alt={alt} loading="lazy" style={{ y, willChange: "transform" }}
        className={`absolute inset-0 w-full h-[112%] -top-[6%] object-cover ${ausrichtung}`} />
    </motion.div>
  );
};

// Überschrift mit Linie, die sich aufzieht
export const LinieTitel = ({ kicker, titel, ton = "#C8743F", hell = false, className = "", testid }) => (
  <motion.div className={className} data-testid={testid}
    initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}>
    {kicker && (
      <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: hell ? "#E8B98F" : ton }}>
        {kicker}
      </p>
    )}
    <motion.span className="block h-px mt-4 origin-left" style={{ backgroundColor: `${ton}80` }}
      initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
      transition={{ duration: 1.1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }} />
    <h2 className={`font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mt-6 ${
      hell ? "text-[#FFFBF4]" : ""}`}>
      {titel}
    </h2>
  </motion.div>
);

// Fotoband: wandert ruhig von allein, lässt sich aber mit Finger oder Maus selbst bewegen
export const FotoLaufband = ({ bilder, testid }) => {
  const ref = useRef(null);
  const pause = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let id;
    const lauf = () => {
      if (Date.now() > pause.current) {
        el.scrollLeft += 0.55;
        const haelfte = el.scrollWidth / 2;
        if (el.scrollLeft >= haelfte) el.scrollLeft -= haelfte;
      }
      id = requestAnimationFrame(lauf);
    };
    id = requestAnimationFrame(lauf);

    const anhalten = () => { pause.current = Date.now() + 2500; };
    let ziehtVon = null;
    const runter = (e) => {
      anhalten();
      ziehtVon = { x: e.clientX, links: el.scrollLeft };
      el.style.cursor = "grabbing";
    };
    const bewegen = (e) => {
      if (!ziehtVon) return;
      anhalten();
      el.scrollLeft = ziehtVon.links - (e.clientX - ziehtVon.x);
    };
    const hoch = () => { ziehtVon = null; el.style.cursor = ""; };

    el.addEventListener("touchstart", anhalten, { passive: true });
    el.addEventListener("touchmove", anhalten, { passive: true });
    el.addEventListener("wheel", anhalten, { passive: true });
    el.addEventListener("pointerdown", runter);
    window.addEventListener("pointermove", bewegen);
    window.addEventListener("pointerup", hoch);
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener("touchstart", anhalten);
      el.removeEventListener("touchmove", anhalten);
      el.removeEventListener("wheel", anhalten);
      el.removeEventListener("pointerdown", runter);
      window.removeEventListener("pointermove", bewegen);
      window.removeEventListener("pointerup", hoch);
    };
  }, []);

  return (
    <div data-testid={testid} className="relative">
      <div ref={ref} data-lenis-prevent-wheel
        className="flex gap-4 overflow-x-auto overscroll-x-contain py-2 px-6 md:px-12 cursor-grab select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[...bilder, ...bilder].map((b, i) => (
          <figure key={i} className="shrink-0 w-[62vw] sm:w-[34vw] lg:w-[23vw]">
            <div className="overflow-hidden aspect-[4/5] bg-[#2B2320]">
              <img src={b.src} alt={b.titel} loading="lazy" draggable="false"
                className="w-full h-full object-cover opacity-95 pointer-events-none" />
            </div>
            <figcaption className="text-[10px] uppercase tracking-[0.2em] text-[#9B8878] mt-3">
              {b.titel}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
};

// Fülleffekt-Button
export const FuellBtn = ({ children, className = "", ...rest }) => (
  <a {...rest}
    className={`group relative inline-flex items-center justify-center gap-3 overflow-hidden bg-[#C8743F] text-white px-9 py-4 text-[11px] uppercase tracking-[0.22em] ${className}`}>
    <span className="absolute inset-0 bg-[#2B2320] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
    <span className="relative inline-flex items-center gap-3">{children}</span>
  </a>
);

// Zarter Lichtpunkt, der dem Zeiger folgt (nur Maus, bewegt per Transform)
export const Lichtzeiger = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const el = ref.current;
    if (!el) return;
    let ziel = { x: 0, y: 0 };
    let ist = { x: 0, y: 0 };
    let id;
    const move = (e) => { ziel = { x: e.clientX, y: e.clientY }; };
    const lauf = () => {
      ist.x += (ziel.x - ist.x) * 0.12;
      ist.y += (ziel.y - ist.y) * 0.12;
      el.style.transform = `translate3d(${ist.x - 150}px, ${ist.y - 150}px, 0)`;
      id = requestAnimationFrame(lauf);
    };
    window.addEventListener("pointermove", move, { passive: true });
    id = requestAnimationFrame(lauf);
    return () => { window.removeEventListener("pointermove", move); cancelAnimationFrame(id); };
  }, []);
  return (
    <div ref={ref} aria-hidden
      className="pointer-events-none fixed left-0 top-0 z-[55] hidden md:block will-change-transform"
      style={{
        width: 300, height: 300,
        background: "radial-gradient(circle, rgba(255,214,170,0.13) 0%, rgba(255,214,170,0) 64%)",
      }} />
  );
};
